<template>
  <div class="snapshot">
    <canvas class="snapshot-canvas"
            ref="hyCanvas"
            @mousedown="canvasMouseDown"
            @mouseup="canvasMouseUp"
            @mousewheel="canvasMouseWheel"
            @mousemove="canvasMouseMove"
            @mouseleave="canvasMouseLeave">Your browser doesn't support canvas</canvas>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
export default Vue.extend({
  data() {
    return {
      width: this.defaultWidth,
      height: this.defaultHeight,
      // Status
      mouse: {},
      startMouse: {},
      isDrawing: false,
    }
  },
  props: {
    // Configs
    // Apperance
    defaultWidth: {
      default: 1920,
    },
    defaultHeight: {
      default: 1080,
    },
    gridNum: {
      default: 60,
    },
    drawLengthThreshold: {
      // 小于该距离的鼠标拖动会被判定为点击
      default: 25,
    },
    showIntensity: {
      default: false,
    },
    // Colors
    detectResultFillStyle: {
      default: () => ({
        0: 'rgba(0, 0, 0, 0)',
        1: 'rgba(0, 159, 255, 0.196)',
        2: 'rgba(253, 178, 64, 0.39)',
        3: 'rgba(236, 16, 16, 0.39)',
        4: 'rgba(255, 255, 0, 0.39)',
        5: 'rgba(255, 0, 0, 0.39)',
        6: 'rgba(128, 42, 42, 0.39)',
        7: 'rgba(255, 97, 0, 0.39)',
      }),
    },
    rowRangeColor: {
      default: 'rgba(255, 0, 0, 1)',
    },
    intensityColor: {
      default: 'rgba(0, 255, 0, 1)',
    },
    blindColor: {
      default: 'rgba(154, 204, 94, 0.5)',
    },
    labelTypes: {
      default: () => ({
        DEFAULT: {
          category: '默认',
          type: '默认',
          shape: 'line',
          style: {
            default: {
              lineWidth: 1,
              strokeStyle: 'rgba(255,0,0,1)',
            },
            active: {
              lineWidth: 3,
            },
          },
        },
      }),
    },

    // Hover
    hoverTips: {
      type: Function || null,
      default: null,
    },

    // layers
    showImage: {
      default: true,
    },
    showDetect: {
      default: true,
    },
    showLabel: {
      default: true,
    },
    showBlind: {
      default: false,
    },
    showDraw: {
      default: true,
    },

    // draw
    drawType: {
      default: 'positive_default',
    },
    drawRender: {
      default: () => ({}),
    },

    // layers
    // images
    mxData: {
      type: Object,
    },
    // blind
    blind: {
      default: () => ({}),
    },
    highlightGrids: {
      default: () => [],
    },
    // detect
    fullDetectResult: {
      type: Object,
      default: () => {},
    },
    // label
    labelList: {
      default: () => [],
    },

    // Data
    rowRange: {
      default: 0,
    },

    // tools
    // magnifier
    magnifier: {
      default: false,
    },
    magnifierSize: {
      default: 150,
    },
    diameter: {
      default: 3,
    },
    // tips
  },

  mounted() {
    this.ctx.imageSmoothingQuality = 'high'
    this.ctx.strokeStyle = '#fff0'

    // size
    this.getSize()
    window.addEventListener('resize', this.getSize)

    this.updateCanvas()
  },

  computed: {
    canvas() {
      return this.$refs.hyCanvas
    },
    ctx() {
      return this.canvas.getContext('2d')
    },
    drawLength() {
      const deltaX = Math.abs(this.mouse.x - this.startMouse.x)
      const deltaY = Math.abs(this.mouse.y - this.startMouse.y)
      const delta = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      return delta
    },
    previewLabel() {
      const area = this.mapPixelToRatio([
        this.startMouse.x,
        this.startMouse.y,
        this.mouse.x,
        this.mouse.y,
      ])
      return {
        type: this.drawType,
        area,
      }
    },
  },

  methods: {
    getSize() {
      this.width = this.$el.offsetWidth
      this.height = (this.width / this.defaultWidth) * this.defaultHeight
    },
    // PART:
    updateCanvas() {
      this.showImage && this.drawImage()
      this.showDetect && this.drawFullDetectResults()
      this.showBlind && this.drawBlindAreas()

      if (this.showLabel) {
        this.drawLabel(this.labelList)
        this.showDraw &&
          this.drawType &&
          this.isDrawing &&
          this.startMouse &&
          // this.drawLength <= this.drawLengthThreshold && // prevent click draw
          this.drawLabel(Object.assign({ active: true }, this.previewLabel))
      }

      // Tools
      this.magnifier && this.drawMagnifyingGlass()

      window.requestAnimationFrame(this.updateCanvas)
    },

    // draw gray number
    drawIntensity() {
      const mouse = this.mouse
      if (mouse.x !== -1 || mouse.y !== -1) {
        var pixelIntensity = this.ctx.getImageData(mouse.x, mouse.y, 1, 1)
        let grayscale =
          0.2126 * pixelIntensity.data[0] +
          0.7152 * pixelIntensity.data[1] +
          0.0722 * pixelIntensity.data[2]

        const x = mouse.x
        const y = mouse.y
        const intensityStr = `x: ${x} y: ${y}, gray scale ${Math.round(
          grayscale,
        )};`
        this.ctx.font = `${(this.width / this.defaultWidth) * 60}px Arial bold`
        this.ctx.fillStyle = this.intensityColor
        this.ctx.fillText(intensityStr, 50, 75)
      }
    },
    drawBlindAreas() {
      const manually = this.blind.manually
      if (Object.prototype.toString.call(manually) === '[object Object]') {
        const offsetTop = this.rowRange
        const width = this.defaultWidth / manually.col
        const height = (this.defaultHeight - 2 * offsetTop) / manually.row
        manually.shield.forEach((i, idx) => {
          if (i) {
            this.ctx.fillStyle = 'rgba(132, 191, 150, 0.3)'
            this.ctx.strokeStyle = this.ctx.fillStyle
            const row = Math.floor(idx / manually.col)
            const col = idx % manually.col
            this.drawRect([
              col * width,
              row * height + offsetTop,
              (col + 1) * width,
              (row + 1) * height + offsetTop,
            ])
          }
        })
      }
    },
    drawHighlightAreas() {
      const hlGridsPos = this.hlGrids.map(highlightGrid =>
        this.gridToPos(highlightGrid),
      )
      hlGridsPos.forEach(highlightGridPos => {
        this.ctx.fillStyle = this.highlightColor
        this.ctx.strokeStyle = '#0000'
        this.drawRect([highlightGridPos[0], 0, highlightGridPos[1], 1])
      })
    },

    drawImage() {
      if (this.mxData) {
        const mxData = this.mxData
        const canvas = this.canvas
        const width = mxData.width
        const height = mxData.height
        canvas.width = width
        canvas.height = height

        let imgData
        try {
          imgData = this.ctx.createImageData(width, height)
        } catch (e) {
          console.log(e)
          console.log(width, height)
          this.$alert(
            '应用出错，内存不足，请手动刷新本页面<br><strong>建议减少同时打开标注系统的数量</strong>',
            '系统错误',
            {
              confirmButtonText: '确定',
              dangerouslyUseHTMLString: true,
            },
          )
          return
        }
        imgData.data.set(mxData.mx)
        this.ctx.putImageData(imgData, 0, 0, 0, 0, width, height)
      }
    },

    drawFullDetectResults() {
      const fullDetectResult = this.fullDetectResult
      const detectResults = fullDetectResult.detectResults

      if (fullDetectResult) {
        const cols = fullDetectResult.cols
        // In reverse order
        const ys = fullDetectResult.xs
        const xs = fullDetectResult.ys

        for (let i = 0, j = detectResults.length - 1; i < j; i++) {
          const curValue = detectResults[i]
          if (curValue <= 0) {
            continue
          }

          const xid = i % cols
          const yid = Math.floor(i / cols)
          const startX = xs + xid * 32 // 1920
          const startY = ys + yid * 32 // 1080
          const endX = startX + 32 // 1920
          const endY = startY + 32 // 1080
          const markArea = [startX, startY, endX, endY]

          this.ctx.fillStyle = this.detectResultFillStyle[curValue]
          this.ctx.strokeStyle = '#0000'
          this.drawRect(markArea)
        }
      }
    },

    drawTips(x, y) {
      const targetGrid = this.posToGrid(x)
      const targetBlindObj = this.blindAreas.find(
        blindArea => blindArea.grid === targetGrid,
      )
      const tipsDom = this.$refs.tips
      const canvasDom = this.canvas
      if (targetBlindObj) {
        const msg = this.hoverTips(targetBlindObj)
        tipsDom.style.left = x + 25 + 'px'
        tipsDom.style.top = y + 10 + 'px'
        tipsDom.style.opacity = '1'
        canvasDom.style.cursor = 'pointer'
        this.tips = msg
      } else {
        tipsDom.style.opacity = '0'
        canvasDom.style.cursor = 'inherit'
      }
    },
    drawLabel(label) {
      if (!label) return
      const ctx = this.ctx
      let labelList
      label instanceof Array ? (labelList = label) : (labelList = [label])
      labelList.forEach(l => {
        const curLabelStyle = this.labelTypes[l.type]
        if (curLabelStyle) {
          ctx.save()
          Object.assign(ctx, curLabelStyle.style.default)
          l.active && Object.assign(ctx, curLabelStyle.style.active)
          const area = this.mapRatioToPixel(l.area)
          switch (curLabelStyle.shape) {
            case 'line':
              this.drawLine(area)
              break
            case 'rect':
              this.drawRect(area)
              break
          }
          // draw type
          const topPoint = this.topPoint(area)
          ctx.font = '24px Georgia bold'
          ctx.fillStyle = ctx.strokeStyle
          ctx.strokeStyle = '#eee'
          ctx.lineWidth = 0.5
          ctx.strokeText(
            `${curLabelStyle.title}`,
            topPoint[0],
            topPoint[1] - 10,
          )
          ctx.fillText(`${curLabelStyle.title}`, topPoint[0], topPoint[1] - 10)
          ctx.restore()
        }
      })
    },

    toggleHighlight(gridIndex) {
      if (this.multiSelect) {
        const targetIndex = this.hlGrids.findIndex(i => i === gridIndex)
        if (targetIndex === -1) {
          this.hlGrids.push(gridIndex)
        } else {
          this.hlGrids.splice(targetIndex, 1)
        }
      } else {
        if (this.hlGrids[0] === gridIndex) {
          this.hlGrids = []
        } else {
          this.hlGrids = [gridIndex]
        }
      }
    },
    // draw
    drawLine(line) {
      const ctx = this.ctx
      ctx.beginPath()
      ctx.moveTo(line[0], line[1])
      ctx.lineTo(line[2], line[3])
      ctx.stroke()
      ctx.closePath()
    },
    drawRect(markArea) {
      const ctx = this.ctx
      ctx.fillRect(
        markArea[0],
        markArea[1],
        markArea[2] - markArea[0],
        markArea[3] - markArea[1],
      )
      ctx.strokeRect(
        markArea[0],
        markArea[1],
        markArea[2] - markArea[0],
        markArea[3] - markArea[1],
      )
    },
    // tools
    posToGrid(pos) {
      if (pos > 1) {
        pos = pos / this.width
      }
      const perGridWidth = this.width / this.gridNum
      return Math.floor((this.width * pos) / perGridWidth)
    },
    gridToPos(grid) {
      const perGridWidth = this.width / this.gridNum
      return [
        (grid * perGridWidth) / this.width,
        ((grid + 1) * perGridWidth) / this.width,
      ]
    },
    mapRatioToPixel(area) {
      const scaleFactor = [
        this.defaultWidth,
        this.defaultHeight,
        this.defaultWidth,
        this.defaultHeight,
      ]
      return area.map((x, idx) => Number(x) * scaleFactor[idx])
    },
    mapPixelToRatio(area) {
      const scaleFactor = [this.width, this.height, this.width, this.height]
      return area.map((x, idx) => Number(x) / scaleFactor[idx])
    },
    topPoint(points) {
      if (points[1] < points[3]) {
        return [points[0], points[1]]
      } else {
        return [points[2], points[3]]
      }
    },

    // Events
    canvasMouseClick(event) {},

    canvasMouseDown(event) {
      this.isDrawing = true
      this.startMouse = {
        x: event.offsetX,
        y: event.offsetY,
      }

      // startMouse
    },
    canvasMouseMove(event) {
      const mouse = {
        x: event.offsetX,
        y: event.offsetY,
      }
      this.mouse = mouse
      this.$emit('onMove', mouse)
    },
    canvasMouseUp(event) {
      if (this.showDraw && this.isDrawing) {
        // 距离在阈值内，判定为点击
        if (this.drawLength <= this.drawLengthThreshold) {
          // emit as click
          this.$emit('onClick', this.startMouse)
        } else {
          // emit as draw
          if (this.drawType) {
            this.$emit('onDraw', this.previewLabel)
          }
        }
      }
      this.isDrawing = false
    },
    canvasMouseWheel(e) {},
    canvasMouseLeave(event) {
      if (
        this.showDraw &&
        this.isDrawing &&
        this.drawLength > this.drawLengthThreshold &&
        this.drawType
      ) {
        // finish draw when cursor leave canvas
        this.$emit('onDraw', this.previewLabel)
      }
      this.isDrawing = false
    },

    drawMagnifyingGlass() {
      const context = this.ctx
      const canvas = this.canvas
      const centerPoint = {
        x: (this.mouse.x * this.defaultWidth) / this.width || 0,
        y: (this.mouse.y * this.defaultHeight) / this.height || 0,
      }
      const originalRadius = this.magnifierSize
      const originalRectangle = { x: 0, y: 0, width: 0, height: 0 }
      const scale = this.diameter

      originalRectangle.x = centerPoint.x - originalRadius
      originalRectangle.y = centerPoint.y - originalRadius
      originalRectangle.width = originalRadius * 2
      originalRectangle.height = originalRadius * 2
      const scaleGlassRectangle = {
        x: centerPoint.x - (originalRectangle.width * scale) / 2,
        y: centerPoint.y - (originalRectangle.height * scale) / 2,
        width: originalRectangle.width * scale,
        height: originalRectangle.height * scale,
      }
      context.save()
      context.beginPath()
      context.arc(
        centerPoint.x,
        centerPoint.y,
        originalRadius,
        0,
        Math.PI * 2,
        false,
      )
      context.clip()
      context.drawImage(
        canvas,
        originalRectangle.x,
        originalRectangle.y,
        originalRectangle.width,
        originalRectangle.height,
        scaleGlassRectangle.x,
        scaleGlassRectangle.y,
        scaleGlassRectangle.width,
        scaleGlassRectangle.height,
      )
      context.restore()
      context.beginPath()
      var gradient = context.createRadialGradient(
        centerPoint.x,
        centerPoint.y,
        originalRadius - 5,
        centerPoint.x,
        centerPoint.y,
        originalRadius,
      )
      gradient.addColorStop(0, 'rgba(0,0,0,0.2)')
      gradient.addColorStop(0.8, 'silver')
      gradient.addColorStop(0.9, 'silver')
      gradient.addColorStop(1.0, 'rgba(150,150,150,0.9)')
      context.strokeStyle = gradient
      context.lineWidth = 5
      context.arc(
        centerPoint.x,
        centerPoint.y,
        originalRadius,
        0,
        Math.PI * 2,
        false,
      )
      context.stroke()
    },
  },
})
</script>

<style lang="scss" scoped>
.snapshot {
  width: 100%;
  height: 100%;
  &-canvas {
    width: 100%;
    height: 100%;
  }
  &-tips {
    position: absolute;
    transition: all 175ms;
    border-radius: 2px;
    overflow: hidden;
    opacity: 0;
    display: flex;
    align-items: center;
    background: white;
    padding: 5px;
  }
}
</style>
